<ng-container *transloco="let t">
  <q-page-header
    [title]="isNew() ? 'createEvent' : 'editEvent'"
    [buttons]="toolbarButtons()"
    (backClick)="cancel()">
  </q-page-header>

  <!-- Loading State -->
  @if (event.isLoading() && !isNew()) {
    <div class="loading-container">
      <div class="spinner-wrapper">
        <mat-spinner diameter="48"></mat-spinner>
        <p class="loading-text regular-14">{{ t('loading') }}</p>
      </div>
    </div>
  }

  <!-- Event Form (only show when not loading or when creating new) -->
  @if (!event.isLoading() || isNew()) {
    <div class="event-editor-container">
      <form [formGroup]="eventForm" class="event-form">
      <!-- Content Selection -->
      <section class="form-section">
        <h2 class="section-title semibold-18">{{ t('eventContent') }}</h2>
        <p class="section-description regular-14">{{ t('selectEventContent') }}</p>

        @if (selectedQuest() || selectedObelisk() || selectedCompilation()) {
          <div class="selected-content-card">
            <div class="content-info">
              <span class="content-type-badge">{{ getContentTypeName() | transloco }}</span>
              <h3 class="content-name semibold-16">{{ getContentName() }}</h3>
            </div>
            <button type="button" class="warning-bar-btn" (click)="removeSelectedContent()">
              {{ t('remove') }}
            </button>
          </div>
        } @else {
          <div class="content-selection-buttons">
            <p class="regular-14 color-soft-grey">{{ t('noContentSelectedYet') }}</p>
            <div class="button-group">
              <button type="button" class="secondary-btn" (click)="openContentSearchModal('quest')">
                + {{ t('addQuest') }}
              </button>
              <button type="button" class="secondary-btn" (click)="openContentSearchModal('compilation')">
                + {{ t('addTrip') }}
              </button>
            </div>
          </div>
        }
      </section>

      <!-- Basic Information -->
      <section class="form-section">
        <h2 class="section-title semibold-18">{{ t('basicInformation') }}</h2>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>{{ t('eventName') }}</mat-label>
          <input matInput formControlName="name" [placeholder]="t('enterEventName')" required>
          @if (eventForm.get('name')?.invalid && eventForm.get('name')?.touched) {
            <mat-error>{{ t('eventNameRequired') }}</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>{{ t('description') }}</mat-label>
          <textarea matInput formControlName="description" rows="4" [placeholder]="t('eventDescription')"></textarea>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>{{ t('maximumParticipants') }}</mat-label>
          <input matInput type="number" formControlName="slots" min="2" required>
          @if (eventForm.get('slots')?.invalid && eventForm.get('slots')?.touched) {
            <mat-error>{{ t('minimumParticipantsRequired') }}</mat-error>
          }
        </mat-form-field>
      </section>

      <!-- Event Settings -->
      <section class="form-section">
        <h2 class="section-title semibold-18">{{ t('eventSettings') }}</h2>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>{{ t('visibility') }}</mat-label>
          <mat-select formControlName="visibility" required>
            @for (option of visibilityOptions; track option.value) {
              <mat-option [value]="option.value">{{ option.label | transloco }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>{{ t('access') }}</mat-label>
          <mat-select formControlName="access" required>
            @for (option of accessOptions; track option.value) {
              <mat-option [value]="option.value">{{ option.label | transloco }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      </section>

      <!-- Dates -->
      <section class="form-section">
        <h2 class="section-title semibold-18">{{ t('dates') }}</h2>

        <div class="datetime-field">
          <label class="field-label regular-14">{{ t('startDate') }} ({{ t('optional') }})</label>
          <input
            type="datetime-local"
            class="datetime-input"
            formControlName="startDate">
        </div>

        <div class="datetime-field">
          <label class="field-label regular-14">{{ t('endDate') }} <span class="required-asterisk">*</span></label>
          <input
            type="datetime-local"
            class="datetime-input"
            formControlName="endDate"
            required>
          @if (eventForm.get('endDate')?.invalid && eventForm.get('endDate')?.touched) {
            <span class="error-message regular-12">{{ t('endDateRequired') }}</span>
          }
        </div>

        <div class="datetime-field">
          <label class="field-label regular-14">{{ t('registrationEndDate') }} ({{ t('optional') }})</label>
          <input
            type="datetime-local"
            class="datetime-input"
            formControlName="registrationEndDate">
        </div>
      </section>

      <!-- Milestones (for compilation events) -->
      @if (selectedCompilation()) {
        <section class="form-section">
          <h2 class="section-title semibold-18">{{ t('milestones') }}</h2>
          <p class="section-description regular-14">{{ t('addMilestonesHelp') }}</p>

          <div class="milestones-list">
            @for (milestone of milestones(); track $index) {
              <div class="milestone-editor-card">
                <div class="milestone-fields">
                  <mat-form-field appearance="outline">
                    <mat-label>{{ t('name') }}</mat-label>
                    <input
                      matInput
                      [value]="milestone.name"
                      (input)="updateMilestone($index, 'name', $any($event.target).value)"
                      [placeholder]="t('milestoneName')">
                  </mat-form-field>

                  <mat-form-field appearance="outline">
                    <mat-label>{{ t('percentage') }}</mat-label>
                    <input
                      matInput
                      type="number"
                      min="0"
                      max="100"
                      [value]="milestone.percentage"
                      (input)="updateMilestone($index, 'percentage', $any($event.target).value)">
                    <span matTextSuffix>%</span>
                  </mat-form-field>
                </div>

                <button type="button" class="warning-bar-btn" (click)="removeMilestone($index)">
                  {{ t('remove') }}
                </button>
              </div>
            }
          </div>

          @if (milestones().length < 5) {
            <button type="button" class="secondary-btn" (click)="addMilestone()">
              + {{ t('addMilestone') }}
            </button>
          }
        </section>
      }

      <!-- Event Admins Section (only for existing events) -->
      @if (!isNew()) {
        <section class="form-section">
          <h2 class="section-title semibold-18">{{ t('eventAdmins') }}</h2>
          <p class="section-description regular-14">{{ t('eventAdminsDescription') }}</p>

          <div class="admins-list">
            @if (adminsList().length === 0) {
              <p class="regular-14 color-soft-grey">{{ t('noAdminsYet') }}</p>
            }

            @for (admin of adminsList(); track admin.id) {
              <app-user-list-item [user]="{
                id: admin.userId,
                username: admin.username,
                email: admin.email,
                leveling: { level: -1 }
              }">
                <div actions>
                  <button type="button" class="warning-bar-btn" (click)="removeAdmin(admin)">
                    {{ t('remove') }}
                  </button>
                </div>
              </app-user-list-item>
            }
          </div>

          <button type="button" class="secondary-btn" (click)="openAddAdminModal()">
            + {{ t('addAdmin') }}
          </button>
        </section>
      }
    </form>

    <!-- Add Admin Modal -->
    @if (showAddAdminModal()) {
      <app-modal
        [title]="t('addAdmin')"
        [footerButtons]="addAdminModalButtons"
        (closeEvent)="closeAddAdminModal()">
        <div class="add-admin-content">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>{{ t('searchUsers') }}</mat-label>
            <input matInput [formControl]="userSearchControl" [placeholder]="t('enterUsernameOrEmail')">
            @if (isSearching()) {
              <mat-icon matIconSuffix>hourglass_empty</mat-icon>
            }
          </mat-form-field>

          <div class="search-results">
            @if (searchResults().length === 0 && userSearchControl.value.length >= 2 && !isSearching()) {
              <p class="regular-14 color-soft-grey">{{ t('noUsersFound') }}</p>
            }

            @for (user of searchResults(); track user.id) {
              <app-user-list-item [user]="user">
                <div actions>
                  <button type="button" class="primary-bar-btn" (click)="addAdmin(user)">
                    {{ t('add') }}
                  </button>
                </div>
              </app-user-list-item>
            }
          </div>
        </div>
      </app-modal>
    }

    <!-- Content Search Modal -->
    @if (showContentSearchModal()) {
      <app-modal
        [title]="t('select' + (contentSearchType() === 'quest' ? 'Quest' : 'Trip'))"
        [footerButtons]="contentSearchModalButtons"
        (closeEvent)="closeContentSearchModal()">
        <div class="content-search-content">
          <mat-form-field appearance="outline" class="full-width margin-top-12">
            <mat-label>{{ t('search') }}</mat-label>
            <input matInput [formControl]="contentSearchControl" [placeholder]="t('enterSearchTerm')">
            @if (isContentSearching()) {
              <mat-icon matIconSuffix>hourglass_empty</mat-icon>
            }
          </mat-form-field>

          <div class="search-results">
            @if (contentSearchResults().length === 0 && contentSearchControl.value.length >= 2 && !isContentSearching()) {
              <p class="regular-14 color-soft-grey">{{ t('noResultsFound') }}</p>
            }

            @for (item of contentSearchResults(); track item.id) {
              <div class="content-result-item" (click)="isQuest(item) ? selectQuest(item) : selectCompilation(item)">
                <div class="content-result-info">
                  <span class="content-type-badge small">
                    {{ isQuest(item) ? t('quest') : t('trip') }}
                  </span>
                  <h4 class="semibold-14">{{ item.name }}</h4>
                  @if (item.description) {
                    <p class="regular-12 color-soft-grey">{{ item.description }}</p>
                  }
                </div>
              </div>
            }
          </div>
        </div>
      </app-modal>
    }
    </div>
  }
</ng-container>


